import { MetaTag, ScriptTag } from '@thd-nucleus/thd-helmet';
import {
  getAllowedNoFollow,
  getDescription,
  getTitleTag
} from '../../helpers/browse-helpers';
import { getBreadcrumbStructuredData } from '../../structured-data/build-breadcrumb-data';
import {
  getBrowseSearchStructuredData,
  getEmtStructuredData
} from '../../structured-data/build-browse-search-structured-data';

const getSchemaValues = (data) => {
  if (!data) return {};
  try {
    const schema = JSON.parse(data);
    return { schema };
  } catch (error) {
    return {};
  }
};

export const getMetadataValues = (emtMetadataResponse = {}) => {
  const { schema = '' } = getSchemaValues(emtMetadataResponse?.schema);

  return {
    emtTitle: emtMetadataResponse?.title,
    emtMetaDescription: emtMetadataResponse?.metaDescription,
    emtOgTitle: emtMetadataResponse?.ogTitle,
    emtOgDescription: emtMetadataResponse?.ogDescription,
    emtOgType: emtMetadataResponse?.ogType,
    emtOgImage: emtMetadataResponse?.ogImage,
    emtSchema: schema
  };
};

export const getCanonicalUrlForPriceRange = (priceRangeRefinement, location, defaultCanonical) => {
  if (!priceRangeRefinement) return null;

  const { pathname = '', search = '' } = location;
  const searchParams = new URLSearchParams(search);

  const lowerBound = searchParams.get('lowerBound') || searchParams.get('lowerbound') || 0;
  const upperBound = searchParams.get('upperBound') || searchParams.get('upperbound') || 0;
  const priceKey = `lb-${lowerBound}-ub-${upperBound}`;

  if (!priceRangeRefinement[priceKey]) return null;

  // since we have a match in the file (line above not null), use the searchModel value for canonical
  const {
    pathname: canonicalPathname,
    origin,
    searchParams: canonicalSearchParams
  } = new URL(defaultCanonical);

  if (canonicalSearchParams?.size <= 2) return defaultCanonical;

  // If there's more than 2 params, that means there's more than lower and upper bound.
  // In that case, reset canonical using searchModel canonical and setting only lower and upper bound params.
  return `${origin}${canonicalPathname}?lowerBound=${lowerBound}&upperBound=${upperBound}`;
};

export const getRobotsTag = (
  pathWithQueryParams,
  canonicalUrl,
  priceRangeExceptionCanonical,
  searchPageIndexingExceptionCanonical
) => {
  const isNoFollow = priceRangeExceptionCanonical || searchPageIndexingExceptionCanonical
    ? false
    : getAllowedNoFollow(pathWithQueryParams, canonicalUrl);

  return new MetaTag({
    name: isNoFollow ? 'ROBOTS' : 'robots',
    content: isNoFollow
      ? 'NOINDEX, NOFOLLOW'
      : 'max-image-preview:large',
    id: 'robots',
  });
};

export const getScriptTag = (
  isContentPageOnly,
  processedBreadcrumbs,
  title,
  canonical,
  emtData,
  emtSchema,
  products
) => {
  if (isContentPageOnly) {
    const breadcrumbStructuredData = getBreadcrumbStructuredData({
      canonical,
      breadcrumbs: processedBreadcrumbs,
      title
    });

    return new ScriptTag({ content: breadcrumbStructuredData, id: 'breadcrumbStructuredData' });
  }
  const faqs = emtData?.emtContent?.content?.questionAndAnswer;
  const schema = emtSchema
    ? getEmtStructuredData({
      faqs,
      schema: emtSchema,
      breadcrumbs: processedBreadcrumbs
    })
    : getBrowseSearchStructuredData({
      breadcrumbs: processedBreadcrumbs,
      canonical,
      faqs,
      skus: products,
      title
    });

  return new ScriptTag({ content: schema, id: 'browseSearchStructuredData' });

};

export const getType = (isTableView, isContentPageOnly, isBrandPage, isHybrid) => {
  let type = 'PLP';
  if (isTableView) {
    type = 'PLP Table View';
  }
  if (isContentPageOnly) {
    type = isBrandPage ? 'Category brand' : 'Category';
  } else if (isHybrid) {
    type = isBrandPage ? 'cat PLP brand' : 'cat PLP';
    if (isTableView && type === 'cat PLP') {
      type = 'cat PLP Table View';
    }
  } else if (isBrandPage) {
    type = 'PLP brand';
  }

  return type;
};

export const getBrowseSearchTitle = ({
  isContentPageOnly,
  parsedTemplates,
  pageType,
  categories,
  refinements,
  searchReportKeyword,
  pathWithQueryParams
}) => {
  if (isContentPageOnly) {
    return parsedTemplates?.options?.metaData?.title;
  }

  return getTitleTag({
    pageType, categories, refinements, searchVal: searchReportKeyword, pathWithQueryParams
  });
};

export const getBrowseSearchDescription = ({
  pageType, categories, refinements, searchReportKeyword, isContentPageOnly, parsedTemplates
}) => {
  if (isContentPageOnly && !(categories[0] === 'Appliances' && pageType === 'b')) {
    return parsedTemplates?.options?.metaData?.description;
  }
  return getDescription(pageType, categories, refinements, searchReportKeyword);
};

export const isCanonicalUrlValid = (canonicalUrl) => {
  if (!canonicalUrl) return false;
  if (canonicalUrl.includes('undefined')) return false;
  if (canonicalUrl.includes('null')) return false;

  return true;
};

export const isObjectValid = (obj) => {
  if (!obj) return false;
  if (Object.keys(obj).length === 0) return false;

  return true;
};

export const isTitleValid = (title) => {
  if (!title) return false;
  if (title.trim() === '') return false;

  return true;
};
